import React, {useState, useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {jsPDF} from 'jspdf';

const {FiDownload, FiFileText, FiCalendar, FiUser, FiLogOut} = FiIcons;

const UserDashboard = ({user, onLogout}) => {
  const [reports, setReports] = useState([]);

  useEffect(() => {
    const allReports = JSON.parse(localStorage.getItem('profit_reports') || '[]');
    const userReports = allReports.filter(report => report.userId === user.id);
    setReports(userReports);
  }, [user.id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const downloadReport = async (report) => {
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const reportData = report.data;
      const logoUrl = "https://ik.imagekit.io/cy1p3cqmc/endpoint/images/Logo/local-Trust-Guide-Logo.png";

      const img = new Image();
      img.src = logoUrl;
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      pdf.addImage(img, 'PNG', 20, 10, 40, 20);
      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.text("Local Trust Guide", 70, 20);
      
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text("We help restaurants grow without depending on ads.", 70, 28);

      pdf.setFontSize(14);
      pdf.setTextColor(0, 0, 0);
      pdf.text("Restaurant Profit Analysis Report", 20, 45);

      pdf.setFontSize(12);
      pdf.text(`Restaurant: ${reportData.restaurant}`, 20, 60);
      pdf.text(`Owner: ${reportData.owner}`, 20, 70);
      pdf.text(`Date: ${new Date(report.createdAt).toLocaleDateString()}`, 20, 80);

      pdf.setFontSize(14);
      pdf.setTextColor(0, 51, 102);
      pdf.text('Profit Summary', 20, 100);
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      const profitData = reportData.profitData || {};
      pdf.text(`Daily Profit: ${formatCurrency(profitData.daily || 0)}`, 25, 115);
      pdf.text(`Weekly Profit: ${formatCurrency(profitData.weekly || 0)}`, 25, 125);
      pdf.text(`Monthly Profit: ${formatCurrency(profitData.monthly || 0)}`, 25, 135);
      pdf.text(`Profit Margin: ${(profitData.margin || 0).toFixed(1)}%`, 25, 145);

      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text('© 2024 Local Trust Guide. We help restaurants grow without depending on ads.', 20, 270);

      const filename = `${report.name.replace(/\s+/g, '_')}.pdf`;
      pdf.save(filename);
    } catch (err) {
      console.error("Error generating PDF:", err);
      alert("Failed to download report. Please try again.");
    }
  };

  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{scale: 0.9, opacity: 0}}
        animate={{scale: 1, opacity: 1}}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
      >
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white bg-opacity-20 p-3 rounded-xl">
                <SafeIcon icon={FiUser} className="text-2xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{user.name}</h2>
                <p className="text-green-100">{user.restaurantName}</p>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-4 py-2 bg-white text-green-600 rounded-lg hover:bg-green-50 transition-colors"
            >
              <SafeIcon icon={FiLogOut} className="text-sm" />
              Logout
            </button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          <h3 className="text-xl font-bold text-slate-800 mb-6">Your Profit Reports</h3>
          {reports.length > 0 ? (
            <div className="space-y-4">
              {reports.map((report) => (
                <motion.div
                  key={report.id}
                  initial={{opacity: 0, y: 10}}
                  animate={{opacity: 1, y: 0}}
                  className="bg-slate-50 rounded-xl p-5 border border-slate-200 hover:border-green-300 transition-all"
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-100 p-3 rounded-lg">
                        <SafeIcon icon={FiFileText} className="text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800">{report.name}</h4>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <SafeIcon icon={FiCalendar} className="text-xs" />
                          <span>{formatDate(report.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => downloadReport(report)}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <SafeIcon icon={FiDownload} className="text-sm" />
                      Download
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-slate-50 rounded-xl">
              <div className="bg-slate-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <SafeIcon icon={FiFileText} className="text-2xl text-slate-400" />
              </div>
              <h4 className="text-lg font-medium text-slate-700 mb-2">No Reports Yet</h4>
              <p className="text-slate-500 max-w-md mx-auto">
                Use the profit calculator to analyze your restaurant's performance and save reports for easy access.
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default UserDashboard;